import React, { useEffect, useState } from "react";
import { Navigate, Routes, Route, NavLink, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard, Calendar, Users, Settings as SettingsIcon, Image, MessageSquare,
  Mail, Gift, Star, Tag, LogOut, Check, X, Send, Cake, TrendingUp, Package,
  Share2, KeyRound, FolderOpen, Sparkles, BookOpen, Clock, Crown
} from "lucide-react";
import { useAuth, authHeaders } from "../AuthContext";
import { useLang } from "../LanguageContext";
import { useSettings } from "../SettingsContext";
import ImagePicker from "../components/ImagePicker";
import FontPicker from "../components/FontPicker";
import { SortableList } from "../components/SortableList";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/earnings", icon: TrendingUp, label: "Earnings" },
  { to: "/admin/bookings", icon: Calendar, label: "Bookings" },
  { to: "/admin/waitlist", icon: Clock, label: "Wait-list" },
  { to: "/admin/members", icon: Crown, label: "Inner Circle" },
  { to: "/admin/clients", icon: Users, label: "Clients" },
  { to: "/admin/birthdays", icon: Cake, label: "Birthdays" },
  { to: "/admin/services", icon: Tag, label: "Services" },
  { to: "/admin/inventory", icon: Package, label: "Inventory" },
  { to: "/admin/media", icon: FolderOpen, label: "Media" },
  { to: "/admin/pages", icon: LayoutDashboard, label: "Page Editor" },
  { to: "/admin/appearance", icon: Sparkles, label: "Appearance" },
  { to: "/admin/gallery", icon: Image, label: "Gallery" },
  { to: "/admin/journal", icon: BookOpen, label: "Journal" },
  { to: "/admin/testimonials", icon: Star, label: "Testimonials" },
  { to: "/admin/referrals", icon: Share2, label: "Referrals" },
  { to: "/admin/leads", icon: Sparkles, label: "Leads" },
  { to: "/admin/gift-cards", icon: Gift, label: "Gift Cards" },
  { to: "/admin/messages", icon: MessageSquare, label: "Messages" },
  { to: "/admin/newsletter", icon: Mail, label: "Newsletter" },
  { to: "/admin/settings", icon: SettingsIcon, label: "Settings" },
  { to: "/admin/password", icon: KeyRound, label: "Password" },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/account" replace />;

  return (
    <div data-testid="admin-layout" className="min-h-screen pt-20 flex">
      <aside className="hidden md:flex flex-col w-64 border-r border-border bg-muted/30 fixed top-20 bottom-0 left-0 px-6 py-8 overflow-y-auto qw-scroll">
        <p className="qw-overline mb-1">Studio</p>
        <p className="font-serif text-xl text-foreground mb-8">Admin</p>
        <nav className="space-y-1 flex-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-[0.78rem] tracking-wider uppercase transition-colors ${
                  isActive ? "bg-primary/15 text-primary border-l-2 border-primary"
                          : "text-foreground/75 hover:text-primary border-l-2 border-transparent"
                }`
              }
            >
              <n.icon size={14} strokeWidth={1.2} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <button
          onClick={() => { logout(); nav("/"); }}
          data-testid="admin-logout-btn"
          className="flex items-center gap-3 px-3 py-2.5 text-[0.78rem] tracking-wider uppercase text-muted-foreground hover:text-destructive transition"
        >
          <LogOut size={14} strokeWidth={1.2} /> Logout
        </button>
      </aside>
      <main className="flex-1 md:ml-64 p-6 md:p-10 lg:p-14 max-w-6xl">
        <Routes>
          <Route index element={<AdminDashboard />} />
          <Route path="earnings" element={<AdminEarnings />} />
          <Route path="bookings" element={<AdminBookings />} />
          <Route path="waitlist" element={<AdminWaitlist />} />
          <Route path="members" element={<AdminMembers />} />
          <Route path="clients" element={<AdminClients />} />
          <Route path="birthdays" element={<AdminBirthdays />} />
          <Route path="services" element={<AdminServices />} />
          <Route path="inventory" element={<AdminInventory />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="pages" element={<AdminPages />} />
          <Route path="appearance" element={<AdminAppearance />} />
          <Route path="gallery" element={<AdminGallery />} />
          <Route path="testimonials" element={<AdminTestimonials />} />
          <Route path="gift-cards" element={<AdminGiftCards />} />
          <Route path="messages" element={<AdminMessages />} />
          <Route path="newsletter" element={<AdminNewsletter />} />
          <Route path="media" element={<AdminMedia />} />
          <Route path="journal" element={<AdminJournal />} />
          <Route path="referrals" element={<AdminReferrals />} />
          <Route path="leads" element={<AdminLeads />} />
          <Route path="password" element={<AdminPassword />} />
        </Routes>
        <div className="md:hidden flex flex-wrap gap-2 mt-12 pt-8 border-t border-border">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} className="px-3 py-1.5 text-[0.7rem] tracking-wider uppercase border border-border hover:border-primary hover:text-primary">
              {n.label}
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}

/* ---------------- Dashboard ---------------- */
function AdminDashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [inventoryAlert, setInventoryAlert] = useState({ low_stock_count: 0, items: [] });

  useEffect(() => {
    axios.get(`${API}/admin/stats`, { headers: authHeaders(token) })
      .then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/admin/analytics`, { headers: authHeaders(token) })
      .then((r) => setAnalytics(r.data)).catch(() => {});
    axios.get(`${API}/admin/inventory`, { headers: authHeaders(token) })
      .then((r) => setInventoryAlert({
        low_stock_count: r.data.low_stock_count || 0,
        items: (r.data.items || []).filter((i) => i.is_low),
      }))
      .catch(() => {});
  }, [token]);

  if (!stats) return <p className="text-muted-foreground">Loading…</p>;

  const cards = [
    { label: "Total Bookings", v: stats.total_bookings, testid: "stat-total-bookings" },
    { label: "Pending", v: stats.pending_bookings, testid: "stat-pending" },
    { label: "Confirmed", v: stats.confirmed_bookings, testid: "stat-confirmed" },
    { label: "Clients", v: stats.total_clients, testid: "stat-clients" },
    { label: "Revenue (FCFA)", v: stats.revenue_fcfa.toLocaleString("fr-FR"), testid: "stat-revenue" },
    { label: "Newsletter Subs", v: stats.newsletter_subscribers, testid: "stat-newsletter" },
    { label: "Contact Messages", v: stats.contact_messages, testid: "stat-messages" },
    { label: "Gift Cards", v: stats.gift_cards, testid: "stat-gift-cards" },
  ];

  // analytics traffic cards
  const trafficCards = analytics ? [
    { label: "Visitors today", v: analytics.visitors.today, testid: "stat-visitors-today" },
    { label: "Views today", v: analytics.views.today, testid: "stat-views-today" },
    { label: "Visitors · 7d", v: analytics.visitors.last_7d, testid: "stat-visitors-7d" },
    { label: "Views · 7d", v: analytics.views.last_7d, testid: "stat-views-7d" },
    { label: "Visitors · 30d", v: analytics.visitors.last_30d, testid: "stat-visitors-30d" },
    { label: "Views · 30d", v: analytics.views.last_30d, testid: "stat-views-30d" },
    { label: "Total views", v: analytics.views.all_time, testid: "stat-views-all" },
  ] : [];

  const maxDaily = analytics ? Math.max(1, ...analytics.daily_14d.map((d) => d.views)) : 1;

  return (
    <div data-testid="admin-dashboard">
      <p className="qw-overline mb-3">Overview</p>
      <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-12 font-light">Dashboard</h1>

      {/* Low stock alert */}
      {inventoryAlert.low_stock_count > 0 && (
        <Link
          to="/admin/inventory"
          data-testid="low-stock-alert"
          className="block mb-10 border border-destructive/50 bg-destructive/5 px-6 py-4 hover:bg-destructive/10 transition-colors"
        >
          <div className="flex items-baseline justify-between flex-wrap gap-3">
            <div>
              <p className="qw-overline text-destructive mb-1">Low stock alert</p>
              <p className="text-sm text-foreground/85">
                {inventoryAlert.low_stock_count} {inventoryAlert.low_stock_count === 1 ? "item is" : "items are"} below threshold:
                {" "}<span className="text-muted-foreground">{inventoryAlert.items.slice(0, 4).map((i) => i.name).join(" · ")}{inventoryAlert.items.length > 4 ? " · …" : ""}</span>
              </p>
            </div>
            <span className="text-[0.65rem] tracking-wider uppercase text-destructive">Manage →</span>
          </div>
        </Link>
      )}

      {/* Business stats */}
      <p className="qw-overline mb-4">Business</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border mb-16">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid} className="bg-background p-6">
            <p className="text-xs tracking-wider uppercase text-muted-foreground mb-3">{c.label}</p>
            <p className="font-serif text-3xl text-primary qw-num">{c.v}</p>
          </div>
        ))}
      </div>

      {/* Traffic analytics */}
      {analytics && (
        <>
          <p className="qw-overline mb-4">Website traffic</p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-border border border-border mb-10">
            {trafficCards.map((c) => (
              <div key={c.label} data-testid={c.testid} className="bg-background p-5">
                <p className="text-[0.65rem] tracking-wider uppercase text-muted-foreground mb-2">{c.label}</p>
                <p className="font-serif text-2xl text-primary qw-num">{c.v}</p>
              </div>
            ))}
          </div>

          {/* 14-day trend chart */}
          <div data-testid="analytics-chart" className="border border-border p-6 mb-10">
            <p className="qw-overline mb-6">Last 14 days</p>
            <div className="flex items-end gap-2 h-40">
              {analytics.daily_14d.map((d) => {
                const h = (d.views / maxDaily) * 100;
                const dayLabel = d.date.slice(8);
                return (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-2 group">
                    <div className="relative w-full flex-1 flex items-end">
                      <div
                        className="w-full bg-primary/30 group-hover:bg-primary transition-colors duration-300 relative"
                        style={{ height: `${Math.max(h, 2)}%` }}
                        title={`${d.date}: ${d.views} views, ${d.visitors} visitors`}
                      >
                        <div className="absolute -top-7 left-1/2 -translate-x-1/2 text-[0.65rem] font-serif text-primary opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap qw-num">
                          {d.views}
                        </div>
                      </div>
                    </div>
                    <span className="text-[0.6rem] text-muted-foreground">{dayLabel}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top pages + breakdowns */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="md:col-span-2 border border-border p-6">
              <p className="qw-overline mb-4">Top pages · 30 days</p>
              {analytics.top_pages.length === 0 ? (
                <p className="text-sm text-muted-foreground">No traffic data yet — visitors will appear here.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {analytics.top_pages.map((p) => (
                    <li key={p._id} className="py-3 flex justify-between items-baseline">
                      <span className="font-serif text-base text-foreground">{p._id || "/"}</span>
                      <span className="text-sm text-primary qw-num">{p.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="border border-border p-6 space-y-6">
              <div>
                <p className="qw-overline mb-3">Devices</p>
                {analytics.devices.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No data</p>
                ) : (
                  <ul className="space-y-2 text-sm">
                    {analytics.devices.map((d) => (
                      <li key={d._id} className="flex justify-between">
                        <span className="text-foreground/85 capitalize">{d._id}</span>
                        <span className="text-primary qw-num">{d.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <p className="qw-overline mb-3">Languages</p>
                {analytics.languages.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No data</p>
                ) : (
                  <ul className="space-y-2 text-sm">
                    {analytics.languages.map((l) => (
                      <li key={l._id} className="flex justify-between">
                        <span className="text-foreground/85 uppercase">{l._id}</span>
                        <span className="text-primary qw-num">{l.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <h3 className="qw-overline mb-4">Popular services</h3>
      <ul className="divide-y divide-border border-y border-border">
        {(stats.popular_services || []).map((p) => (
          <li key={p._id} className="py-4 flex justify-between items-baseline">
            <span className="font-serif text-lg text-foreground">{p.name || p._id}</span>
            <span className="text-sm text-primary qw-num">{p.count} bookings</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------- Bookings ---------------- */
function AdminBookings() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);

  const load = () => {
    axios.get(`${API}/admin/bookings`, { headers: authHeaders(token) })
      .then((r) => setItems(r.data.bookings || [])).catch(() => {});
  };
  useEffect(load, [token]);

  const setStatus = async (id, status) => {
    await axios.patch(`${API}/admin/bookings/${id}`, { status }, { headers: authHeaders(token) });
    load();
  };

  const setDeposit = async (id, deposit_status) => {
    await axios.patch(`${API}/admin/bookings/${id}/deposit`, { deposit_status }, { headers: authHeaders(token) });
    load();
  };

  const toggleNoShow = async (b) => {
    await axios.patch(`${API}/admin/bookings/${b.id}/no-show`, { is_no_show: !b.is_no_show }, { headers: authHeaders(token) });
    load();
  };

  const depositStyle = (s) => ({
    received: "border-primary text-primary",
    pending: "border-muted-foreground text-muted-foreground",
    missed: "border-destructive text-destructive",
    none: "border-border text-foreground/60",
  })[s || "pending"];

  return (
    <div data-testid="admin-bookings">
      <p className="qw-overline mb-3">Reservations</p>
      <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-10 font-light">Bookings</h1>
      <ul className="divide-y divide-border border-y border-border">
        {items.length === 0 && <li className="py-6 text-muted-foreground">No bookings yet.</li>}
        {items.map((b) => (
          <li key={b.id} data-testid={`admin-booking-${b.id}`} className="py-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
            <div className="md:col-span-4">
              <p className="font-serif text-lg text-foreground flex items-center gap-2">
                {b.full_name}
                {b.is_member && (
                  <span data-testid={`member-flag-${b.id}`} className="inline-flex items-center gap-1 text-[0.6rem] tracking-wider uppercase px-2 py-0.5 bg-primary/15 text-primary border border-primary/30"><Crown size={9} strokeWidth={1.4} /> Member{b.member_discount_pct ? ` −${b.member_discount_pct}%` : ""}</span>
                )}
                {b.is_no_show && (
                  <span data-testid={`no-show-flag-${b.id}`} className="text-[0.6rem] tracking-wider uppercase px-2 py-0.5 bg-destructive/15 text-destructive border border-destructive/30">No-show</span>
                )}
              </p>
              <p className="text-xs text-muted-foreground">{b.email} · {b.phone}</p>
              {b.referral_code_used && (
                <p className="text-[0.65rem] text-primary mt-1">ref: {b.referral_code_used}</p>
              )}
            </div>
            <div className="md:col-span-3 text-sm">
              <p className="text-foreground">{b.service_name}</p>
              <p className="text-xs text-primary qw-num">{b.service_price}</p>
            </div>
            <div className="md:col-span-2 text-xs text-foreground/85">
              {b.appointment_date}<br />{b.appointment_time}
            </div>
            <div className="md:col-span-3 flex flex-col gap-2 items-start md:items-end">
              <div className="flex flex-wrap gap-2 justify-start md:justify-end">
                <span className={`text-[0.65rem] tracking-wider uppercase px-2 py-1 border ${
                  b.status === "confirmed" ? "border-primary text-primary" :
                  b.status === "declined" ? "border-destructive text-destructive" :
                  b.status === "completed" ? "border-accent text-accent" : "border-muted-foreground text-muted-foreground"
                }`}>{b.status}</span>
                {b.status !== "confirmed" && (
                  <button data-testid={`confirm-${b.id}`} onClick={() => setStatus(b.id, "confirmed")} className="h-7 w-7 border border-primary text-primary hover:bg-primary hover:text-primary-foreground flex items-center justify-center"><Check size={12} strokeWidth={1.2} /></button>
                )}
                {b.status !== "declined" && (
                  <button data-testid={`decline-${b.id}`} onClick={() => setStatus(b.id, "declined")} className="h-7 w-7 border border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground flex items-center justify-center"><X size={12} strokeWidth={1.2} /></button>
                )}
                {b.status !== "completed" && (
                  <button data-testid={`complete-${b.id}`} onClick={() => setStatus(b.id, "completed")} className="text-[0.65rem] tracking-wider uppercase px-2 py-1 border border-accent text-accent hover:bg-accent hover:text-accent-foreground">Done</button>
                )}
              </div>
              {/* Deposit + no-show controls */}
              <div className="flex flex-wrap gap-2 justify-start md:justify-end items-center">
                <span className="text-[0.6rem] tracking-wider uppercase text-muted-foreground">Deposit</span>
                <select
                  data-testid={`deposit-select-${b.id}`}
                  value={b.deposit_status || "pending"}
                  onChange={(e) => setDeposit(b.id, e.target.value)}
                  className={`text-[0.65rem] tracking-wider uppercase px-2 py-1 bg-background border ${depositStyle(b.deposit_status)} focus:outline-none`}
                >
                  <option value="pending">Pending</option>
                  <option value="received">Received</option>
                  <option value="missed">Missed</option>
                  <option value="none">N/A</option>
                </select>
                <button
                  onClick={() => toggleNoShow(b)}
                  data-testid={`no-show-toggle-${b.id}`}
                  className={`text-[0.65rem] tracking-wider uppercase px-2 py-1 border transition-colors ${b.is_no_show ? "border-destructive text-destructive bg-destructive/10" : "border-border text-foreground/70 hover:border-destructive hover:text-destructive"}`}
                >
                  {b.is_no_show ? "Un-flag" : "No-show"}
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------- Clients ---------------- */
function AdminClients() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/clients`, { headers: authHeaders(token) })
      .then((r) => setItems(r.data.clients || [])).catch(() => {});
  }, [token]);
  return (
    <div data-testid="admin-clients">
      <p className="qw-overline mb-3">Membership</p>
      <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-10 font-light">Clients</h1>
      <ul className="divide-y divide-border border-y border-border">
        {items.length === 0 && <li className="py-6 text-muted-foreground">No clients yet.</li>}
        {items.map((c) => (
          <li key={c.id} className="py-5 grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-4 font-serif text-lg">{c.name}</div>
            <div className="md:col-span-4 text-sm text-muted-foreground">{c.email}</div>
            <div className="md:col-span-2 text-xs uppercase tracking-wider text-foreground/85">{c.bookings_count} bookings</div>
            <div className="md:col-span-2 text-xs uppercase tracking-wider text-primary qw-num">{c.loyalty_visits} visits</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------- Services ---------------- */
function AdminServices() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState({});

  const load = () => axios.get(`${API}/services`).then((r) => setItems(r.data.services || []));
  useEffect(() => { load(); }, []);

  const save = async (id) => {
    await axios.put(`${API}/admin/services/${id}`, edit[id], { headers: authHeaders(token) });
    setEdit((p) => { const n = { ...p }; delete n[id]; return n; });
    load();
  };

  const reorder = async (ids, next) => {
    setItems(next); // optimistic
    try {
      await axios.post(`${API}/admin/services/reorder`, { ids }, { headers: authHeaders(token) });
    } catch { load(); }
  };

  return (
    <div data-testid="admin-services">
      <p className="qw-overline mb-3">Catalog</p>
      <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-4 font-light">Services</h1>
      <p className="text-xs text-muted-foreground mb-8">Drag the handles to reorder the list — the new order will appear on the public Services page.</p>
      <div className="divide-y divide-border border-y border-border">
        <SortableList items={items} onReorder={reorder}>
          {(s, handle) => {
            const e = edit[s.id] || {};
            return (
              <li className="py-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-center list-none">
                <div className="md:col-span-1 flex items-center">{handle}</div>
                <div className="md:col-span-3">
                  <input
                    data-testid={`svc-name-en-${s.id}`}
                    className="qw-input !py-1 !text-base"
                    defaultValue={s.name_en}
                    onChange={(ev) => setEdit({ ...edit, [s.id]: { ...e, name_en: ev.target.value } })}
                  />
                  <input
                    data-testid={`svc-name-fr-${s.id}`}
                    className="qw-input !py-1 !text-sm italic mt-1"
                    defaultValue={s.name_fr}
                    onChange={(ev) => setEdit({ ...edit, [s.id]: { ...e, name_fr: ev.target.value } })}
                  />
                </div>
                <div className="md:col-span-3">
                  <input
                    className="qw-input !py-1 !text-base"
                    type="number"
                    defaultValue={s.price_fcfa}
                    onChange={(ev) => setEdit({ ...edit, [s.id]: { ...e, price_fcfa: Number(ev.target.value) } })}
                  />
                  <p className="text-[0.65rem] uppercase tracking-wider text-muted-foreground mt-1">FCFA</p>
                </div>
                <div className="md:col-span-2">
                  <input
                    className="qw-input !py-1 !text-sm"
                    defaultValue={s.duration}
                    onChange={(ev) => setEdit({ ...edit, [s.id]: { ...e, duration: ev.target.value } })}
                  />
                </div>
                <div className="md:col-span-3 text-right">
                  <button
                    data-testid={`save-service-${s.id}`}
                    disabled={!edit[s.id]}
                    onClick={() => save(s.id)}
                    className="qw-btn qw-btn-outline !py-2 !px-4 !text-[0.65rem] disabled:opacity-40"
                  >
                    Save
                  </button>
                </div>
              </li>
            );
          }}
        </SortableList>
      </div>
    </div>
  );
}

/* ---------------- Settings ---------------- */
function AdminSettings() {
  const { token } = useAuth();
  const { refresh } = useSettings();
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/settings`, { headers: authHeaders(token) })
      .then((r) => setS(r.data)).catch(() => {});
  }, [token]);

  if (!s) return <p className="text-muted-foreground">…</p>;

  const upd = (k, v) => setS({ ...s, [k]: v });
  const updHours = (day, k, v) => setS({ ...s, hours: { ...s.hours, [day]: { ...s.hours[day], [k]: v } } });

  const save = async () => {
    await axios.put(`${API}/admin/settings`, s, { headers: authHeaders(token) });
    await refresh();
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

  return (
    <div data-testid="admin-settings">
      <p className="qw-overline mb-3">Site</p>
      <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-10 font-light">Settings</h1>

      <h3 className="qw-overline mb-4 mt-8">Contact</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Phone (display)" value={s.phone} onChange={(v) => upd("phone", v)} />
        <Field label="Phone (E164)" value={s.phone_e164} onChange={(v) => upd("phone_e164", v)} />
        <Field label="WhatsApp (digits only)" value={s.whatsapp_e164} onChange={(v) => upd("whatsapp_e164", v)} />
        <Field label="Email" value={s.email} onChange={(v) => upd("email", v)} />
        <Field label="Instagram URL" value={s.instagram_url} onChange={(v) => upd("instagram_url", v)} />
        <Field label="Instagram handle" value={s.instagram_handle} onChange={(v) => upd("instagram_handle", v)} />
        <Field label="Dakar location" value={s.location_dakar} onChange={(v) => upd("location_dakar", v)} />
        <Field label="London location" value={s.location_london} onChange={(v) => upd("location_london", v)} />
        <Field label="Deposit amount (FCFA)" value={s.deposit_fcfa} onChange={(v) => upd("deposit_fcfa", Number(v))} type="number" />
        <Field label="Deposit method" value={s.deposit_method} onChange={(v) => upd("deposit_method", v)} />
      </div>

      <h3 className="qw-overline mb-4 mt-12">Hours</h3>
      <div className="space-y-3">
        {days.map((d) => (
          <div key={d} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
            <div className="md:col-span-3 font-serif text-lg capitalize">{d}</div>
            <label className="md:col-span-2 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!s.hours?.[d]?.closed}
                onChange={(e) => updHours(d, "closed", e.target.checked)}
              /> Closed
            </label>
            <input className="md:col-span-3 qw-input !py-2" type="time" value={s.hours?.[d]?.open || ""} onChange={(e) => updHours(d, "open", e.target.value)} disabled={s.hours?.[d]?.closed} />
            <input className="md:col-span-3 qw-input !py-2" type="time" value={s.hours?.[d]?.close || ""} onChange={(e) => updHours(d, "close", e.target.value)} disabled={s.hours?.[d]?.closed} />
          </div>
        ))}
      </div>

      <h3 className="qw-overline mb-4 mt-12">Hero & About copy</h3>
      <div className="grid grid-cols-1 gap-6">
        <Field label="Hero title (EN)" value={s.hero_title_en} onChange={(v) => upd("hero_title_en", v)} />
        <Field label="Hero title (FR)" value={s.hero_title_fr} onChange={(v) => upd("hero_title_fr", v)} />
        <Field label="Hero subtitle (EN)" value={s.hero_subtitle_en} onChange={(v) => upd("hero_subtitle_en", v)} multi />
        <Field label="Hero subtitle (FR)" value={s.hero_subtitle_fr} onChange={(v) => upd("hero_subtitle_fr", v)} multi />
        <Field label="About body (EN)" value={s.about_body_en} onChange={(v) => upd("about_body_en", v)} multi />
        <Field label="About body (FR)" value={s.about_body_fr} onChange={(v) => upd("about_body_fr", v)} multi />
      </div>

      <h3 className="qw-overline mb-4 mt-12">Site images</h3>
      <p className="text-xs text-muted-foreground mb-5 max-w-xl">
        Override any image on the site by uploading a new one. Leave blank to use the default
        packaged with the site. Recommended sizes: logo 400×400, hero 1600×2400, studio 1200×1600, category 800×1000.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ImagePicker testid="img-logo-picker" label="Logo (round)" value={s.img_logo || ""} onChange={(v) => upd("img_logo", v)} aspect="square" />
        <ImagePicker testid="img-hero-picker" label="Home hero" value={s.img_hero || ""} onChange={(v) => upd("img_hero", v)} />
        <ImagePicker testid="img-studio-picker" label="Studio / About photo" value={s.img_studio || ""} onChange={(v) => upd("img_studio", v)} />
        <ImagePicker testid="img-botox-picker" label="Botox category" value={s.img_botox || ""} onChange={(v) => upd("img_botox", v)} />
        <ImagePicker testid="img-fillers-picker" label="Fillers category" value={s.img_fillers || ""} onChange={(v) => upd("img_fillers", v)} />
        <ImagePicker testid="img-iv-picker" label="IV Therapy category" value={s.img_iv || ""} onChange={(v) => upd("img_iv", v)} />
        <ImagePicker testid="img-lashes-picker" label="Lashes category" value={s.img_lashes || ""} onChange={(v) => upd("img_lashes", v)} />
        <ImagePicker testid="img-brows-picker" label="Brows category" value={s.img_brows || ""} onChange={(v) => upd("img_brows", v)} />
      </div>

      <h3 className="qw-overline mb-4 mt-12">Currency conversion rates</h3>
      <p className="text-xs text-muted-foreground mb-5 max-w-xl">
        How many units of the target currency 1 FCFA buys. Used by the front-end currency toggle.
        Update these whenever rates drift meaningfully — e.g. once a month.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Field testid="fx-gbp-input" label="1 FCFA → GBP" value={s.fx_fcfa_to_gbp} onChange={(v) => upd("fx_fcfa_to_gbp", Number(v))} type="number" />
        <Field testid="fx-eur-input" label="1 FCFA → EUR" value={s.fx_fcfa_to_eur} onChange={(v) => upd("fx_fcfa_to_eur", Number(v))} type="number" />
        <Field testid="fx-usd-input" label="1 FCFA → USD" value={s.fx_fcfa_to_usd} onChange={(v) => upd("fx_fcfa_to_usd", Number(v))} type="number" />
      </div>

      <div className="mt-12 flex items-center gap-4">
        <button data-testid="settings-save-btn" onClick={save} className="qw-btn qw-btn-primary">Save</button>
        {saved && <span data-testid="settings-saved" className="text-primary text-sm">Saved ✓</span>}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", multi = false, testid }) {
  return (
    <div>
      <label className="qw-overline block mb-2">{label}</label>
      {multi ? (
        <textarea rows="3" data-testid={testid} className="qw-input resize-none" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} data-testid={testid} className="qw-input" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

/* ---------------- Gallery ---------------- */
function AdminGallery() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title_en: "", title_fr: "", category: "lashes", before_url: "", after_url: "" });

  const load = () => axios.get(`${API}/admin/gallery`, { headers: authHeaders(token) }).then((r) => setItems(r.data.gallery || []));
  useEffect(() => { load(); }, [token]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.before_url || !form.after_url) {
      alert("Please upload both a before and an after image.");
      return;
    }
    await axios.post(`${API}/admin/gallery`, form, { headers: authHeaders(token) });
    setForm({ title_en: "", title_fr: "", category: "lashes", before_url: "", after_url: "" });
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this gallery item?")) return;
    await axios.delete(`${API}/admin/gallery/${id}`, { headers: authHeaders(token) });
    load();
  };

  const resolveImg = (u) => (u && u.startsWith("http") ? u : `${BACKEND_URL}${u || ""}`);

  return (
    <div data-testid="admin-gallery">
      <p className="qw-overline mb-3">Showcase</p>
      <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-10 font-light">Before / After</h1>
      <form onSubmit={add} data-testid="gallery-add-form" className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 border-y border-border py-8">
        <Field label="Title (EN)" value={form.title_en} onChange={(v) => setForm({ ...form, title_en: v })} />
        <Field label="Title (FR)" value={form.title_fr} onChange={(v) => setForm({ ...form, title_fr: v })} />
        <div>
          <label className="qw-overline block mb-2">Category</label>
          <select data-testid="gallery-category-select" className="qw-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {["lashes", "brows", "iv", "fillers", "botox"].map((c) => <option key={c} value={c} className="bg-background">{c}</option>)}
          </select>
        </div>
        <div />
        <ImagePicker testid="gallery-before-picker" label="Before image" value={form.before_url} onChange={(v) => setForm({ ...form, before_url: v })} aspect="square" />
        <ImagePicker testid="gallery-after-picker" label="After image" value={form.after_url} onChange={(v) => setForm({ ...form, after_url: v })} aspect="square" />
        <div className="md:col-span-2">
          <button type="submit" data-testid="gallery-add-btn" className="qw-btn qw-btn-primary">Add item</button>
        </div>
      </form>
      <p className="text-xs text-muted-foreground mb-4">Drag to reorder — the new order appears on the public Gallery page.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <SortableList
          items={items}
          strategy="grid"
          onReorder={async (ids, next) => {
            setItems(next);
            try { await axios.post(`${API}/admin/gallery/reorder`, { ids }, { headers: authHeaders(token) }); }
            catch { load(); }
          }}
        >
          {(g, handle) => (
            <div data-testid={`gallery-card-${g.id}`} className="border border-border p-3">
              <div className="flex items-center justify-between mb-2">
                {handle}
                <button data-testid={`delete-gallery-${g.id}`} onClick={() => del(g.id)} className="text-xs uppercase tracking-wider text-destructive">Delete</button>
              </div>
              <div className="grid grid-cols-2 gap-1 mb-2">
                <img src={resolveImg(g.before_url)} className="aspect-square object-cover" alt="" />
                <img src={resolveImg(g.after_url)} className="aspect-square object-cover" alt="" />
              </div>
              <p className="font-serif text-base">{g.title_en}</p>
              <p className="text-xs text-muted-foreground">{g.category}</p>
            </div>
          )}
        </SortableList>
      </div>
    </div>
  );
}

/* ---------------- Testimonials ---------------- */
function AdminTestimonials() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", text: "", rating: 5, service: "" });

  const load = () => axios.get(`${API}/admin/testimonials`, { headers: authHeaders(token) }).then((r) => setItems(r.data.testimonials || []));
  useEffect(() => { load(); }, [token]);

  const add = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/admin/testimonials`, form, { headers: authHeaders(token) });
    setForm({ name: "", text: "", rating: 5, service: "" });
    load();
  };
  const del = async (id) => {
    await axios.delete(`${API}/admin/testimonials/${id}`, { headers: authHeaders(token) });
    load();
  };

  return (
    <div data-testid="admin-testimonials">
      <p className="qw-overline mb-3">Voices</p>
      <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-10 font-light">Testimonials</h1>
      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-2 gap-4 border-y border-border py-8 mb-12" data-testid="testimonial-add-form">
        <Field label="Client name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        <Field label="Service" value={form.service} onChange={(v) => setForm({ ...form, service: v })} />
        <div className="md:col-span-2">
          <Field label="Quote" value={form.text} onChange={(v) => setForm({ ...form, text: v })} multi />
        </div>
        <div>
          <label className="qw-overline block mb-2">Rating (1–5)</label>
          <input type="number" min={1} max={5} className="qw-input" value={form.rating} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })} />
        </div>
        <div className="md:col-span-2">
          <button type="submit" data-testid="testimonial-add-btn" className="qw-btn qw-btn-primary">Add testimonial</button>
        </div>
      </form>
      <ul className="divide-y divide-border border-y border-border">
        {items.map((t) => (
          <li key={t.id} className="py-5 grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-3 font-serif text-lg">{t.name}</div>
            <div className="md:col-span-7 text-sm text-foreground/85 italic">"{t.text}"</div>
            <div className="md:col-span-2 text-right">
              <button data-testid={`delete-testimonial-${t.id}`} onClick={() => del(t.id)} className="text-xs uppercase tracking-wider text-destructive">Delete</button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------- Gift Cards ---------------- */
function AdminGiftCards() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);

  const load = () => axios.get(`${API}/admin/gift-cards`, { headers: authHeaders(token) }).then((r) => setItems(r.data.gift_cards || []));
  useEffect(() => { load(); }, [token]);

  const issue = async (id) => {
    await axios.post(`${API}/admin/gift-cards/${id}/issue`, {}, { headers: authHeaders(token) });
    load();
  };

  return (
    <div data-testid="admin-gift-cards">
      <p className="qw-overline mb-3">Treats</p>
      <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-10 font-light">Gift Cards</h1>
      <ul className="divide-y divide-border border-y border-border">
        {items.length === 0 && <li className="py-6 text-muted-foreground">No requests yet.</li>}
        {items.map((g) => (
          <li key={g.id} data-testid={`gift-${g.id}`} className="py-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
            <div className="md:col-span-3">
              <p className="font-serif text-lg">{g.buyer_name}</p>
              <p className="text-xs text-muted-foreground">{g.buyer_email}</p>
              <p className="text-xs text-muted-foreground">{g.buyer_phone}</p>
            </div>
            <div className="md:col-span-3 text-sm">→ {g.recipient_name}</div>
            <div className="md:col-span-2 qw-num text-primary">{g.amount_fcfa.toLocaleString("fr-FR")} FCFA</div>
            <div className="md:col-span-2 text-xs">{g.code || <span className="text-muted-foreground italic">no code</span>}</div>
            <div className="md:col-span-2 text-right">
              {!g.code && (
                <button data-testid={`issue-${g.id}`} onClick={() => issue(g.id)} className="qw-btn qw-btn-outline !py-2 !px-4 !text-[0.65rem]">Issue code</button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------- Messages ---------------- */
function AdminMessages() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/messages`, { headers: authHeaders(token) }).then((r) => setItems(r.data.messages || []));
  }, [token]);
  return (
    <div data-testid="admin-messages">
      <p className="qw-overline mb-3">Inbox</p>
      <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-10 font-light">Messages</h1>
      <ul className="divide-y divide-border border-y border-border">
        {items.length === 0 && <li className="py-6 text-muted-foreground">No messages.</li>}
        {items.map((m) => (
          <li key={m.id} className="py-5">
            <div className="flex justify-between items-baseline mb-2">
              <span className="font-serif text-lg">{m.full_name}</span>
              <span className="text-xs text-muted-foreground">{m.email}</span>
            </div>
            <p className="text-sm text-foreground/85 leading-relaxed">{m.message}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------- Newsletter ---------------- */
function AdminNewsletter() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/newsletter`, { headers: authHeaders(token) }).then((r) => setItems(r.data.subscribers || []));
  }, [token]);
  return (
    <div data-testid="admin-newsletter">
      <p className="qw-overline mb-3">Subscribers</p>
      <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-10 font-light">Newsletter</h1>
      <ul className="divide-y divide-border border-y border-border">
        {items.length === 0 && <li className="py-6 text-muted-foreground">No subscribers yet.</li>}
        {items.map((s) => (
          <li key={s.id} className="py-3 flex justify-between text-sm">
            <span>{s.email}</span>
            <span className="text-xs text-primary tracking-wider qw-num">{s.welcome_code}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------- Birthdays ---------------- */
function AdminBirthdays() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/birthdays`, { headers: authHeaders(token) }).then((r) => setData(r.data));
  }, [token]);
  if (!data) return <p className="text-muted-foreground">Loading…</p>;
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  return (
    <div data-testid="admin-birthdays">
      <p className="qw-overline mb-3">This month</p>
      <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-2 font-light">Birthdays</h1>
      <p className="text-muted-foreground mb-10">{monthNames[data.month - 1]} {data.year} — {data.birthdays.length} {data.birthdays.length === 1 ? "client" : "clients"}</p>
      {data.birthdays.length === 0 ? (
        <p className="text-muted-foreground py-12 border-y border-border">No birthdays this month.</p>
      ) : (
        <ul className="divide-y divide-border border-y border-border">
          {data.birthdays.map((u) => {
            const day = u.date_of_birth?.split("-")[2];
            return (
              <li key={u.id} data-testid={`birthday-${u.id}`} className="py-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                <div className="md:col-span-1 font-serif text-3xl text-primary qw-num">{day}</div>
                <div className="md:col-span-4">
                  <p className="font-serif text-lg text-foreground">{u.name}</p>
                  <p className="text-xs text-muted-foreground">{u.email}</p>
                </div>
                <div className="md:col-span-2 text-xs text-foreground/85">
                  {u.loyalty_visits} {u.loyalty_visits === 1 ? "visit" : "visits"}
                </div>
                <div className="md:col-span-5 text-right">
                  {u.promo ? (
                    <div>
                      <p className="qw-num text-primary text-sm tracking-wider">{u.promo.code}</p>
                      <p className="text-[0.65rem] text-muted-foreground tracking-wider uppercase mt-1">
                        {u.promo.discount_pct}% · {u.promo.redeemed ? "REDEEMED" : "ACTIVE"} · exp {u.promo.expires_at}
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">Code issues on next login</p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ---------------- Earnings ---------------- */
function fmtFcfa(n) {
  return Number(n || 0).toLocaleString("fr-FR") + " FCFA";
}

function AdminEarnings() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [catPeriod, setCatPeriod] = useState("all"); // all | month | year

  useEffect(() => {
    axios.get(`${API}/admin/earnings`, { headers: authHeaders(token) })
      .then((r) => setData(r.data)).catch(() => {});
  }, [token]);

  if (!data) return <p className="text-muted-foreground">Loading earnings…</p>;

  const big = [
    { label: "Today", v: data.today.revenue, n: data.today.count, testid: "earn-today" },
    { label: "This week", v: data.this_week.revenue, n: data.this_week.count, testid: "earn-week" },
    { label: "This month", v: data.this_month.revenue, n: data.this_month.count, testid: "earn-month" },
    { label: "This year", v: data.this_year.revenue, n: data.this_year.count, testid: "earn-year" },
  ];
  const sec = [
    { label: "Last 7 days", v: data.last_7d.revenue, n: data.last_7d.count, testid: "earn-7d" },
    { label: "Last 30 days", v: data.last_30d.revenue, n: data.last_30d.count, testid: "earn-30d" },
    { label: "All time", v: data.total.revenue, n: data.total.count, testid: "earn-total" },
    { label: "Average ticket", v: data.average_ticket, n: null, testid: "earn-avg" },
  ];

  const maxMonth = Math.max(1, ...data.monthly_series.map((m) => m.revenue));
  const catMap = {
    all: data.category_breakdown || [],
    month: data.category_breakdown_month || [],
    year: data.category_breakdown_year || [],
  };
  const activeCategory = catMap[catPeriod] || [];
  const maxCat = Math.max(1, ...activeCategory.map((c) => c.revenue));
  const catLabel = { all: "all time", month: "this month", year: "this year" }[catPeriod];

  return (
    <div data-testid="admin-earnings">
      <p className="qw-overline mb-3">Revenue</p>
      <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-3 font-light">My earnings</h1>
      <p className="text-muted-foreground mb-12 max-w-xl text-sm">
        Calculated from confirmed and completed bookings — appointment date is the basis.
      </p>

      {/* Big cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border mb-8">
        {big.map((c) => (
          <div key={c.label} data-testid={c.testid} className="bg-background p-6">
            <p className="text-[0.7rem] tracking-wider uppercase text-muted-foreground mb-3">{c.label}</p>
            <p className="font-serif text-2xl md:text-3xl text-primary qw-num leading-tight">{fmtFcfa(c.v)}</p>
            <p className="text-xs text-muted-foreground mt-2">{c.n} {c.n === 1 ? "booking" : "bookings"}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border mb-16">
        {sec.map((c) => (
          <div key={c.label} data-testid={c.testid} className="bg-background p-5">
            <p className="text-[0.65rem] tracking-wider uppercase text-muted-foreground mb-2">{c.label}</p>
            <p className="font-serif text-xl text-primary qw-num leading-tight">{fmtFcfa(c.v)}</p>
            {c.n !== null && <p className="text-xs text-muted-foreground mt-1">{c.n}</p>}
          </div>
        ))}
      </div>

      {/* 12-month chart */}
      <div data-testid="earnings-chart" className="border border-border p-6 md:p-8 mb-12">
        <div className="flex items-baseline justify-between mb-8">
          <p className="qw-overline">Last 12 months</p>
          <p className="text-xs text-muted-foreground tracking-wider">Peak: <span className="text-primary qw-num">{fmtFcfa(maxMonth)}</span></p>
        </div>
        <div className="flex items-end gap-3 h-56">
          {data.monthly_series.map((m) => {
            const h = (m.revenue / maxMonth) * 100;
            return (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-2 group min-w-0">
                <div className="relative w-full flex-1 flex items-end">
                  <div
                    className="w-full bg-primary/30 group-hover:bg-primary transition-colors duration-300 relative"
                    style={{ height: `${Math.max(h, 1.5)}%` }}
                    title={`${m.month}: ${fmtFcfa(m.revenue)} · ${m.bookings} bookings`}
                  >
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                      <p className="text-[0.7rem] font-serif text-primary qw-num">{fmtFcfa(m.revenue)}</p>
                      <p className="text-[0.6rem] text-muted-foreground text-center">{m.bookings} bk</p>
                    </div>
                  </div>
                </div>
                <span className="text-[0.65rem] text-muted-foreground tracking-wider uppercase">{m.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Category breakdown */}
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <p className="qw-overline">Revenue by service category — {catLabel}</p>
        <div data-testid="earn-cat-filter" className="flex gap-px bg-border border border-border">
          {[
            { k: "month", label: "This month" },
            { k: "year", label: "This year" },
            { k: "all", label: "All time" },
          ].map((opt) => (
            <button
              key={opt.k}
              type="button"
              data-testid={`earn-cat-filter-${opt.k}`}
              onClick={() => setCatPeriod(opt.k)}
              className={`px-4 py-2 text-[0.7rem] tracking-wider uppercase transition-colors ${
                catPeriod === opt.k
                  ? "bg-primary text-primary-foreground"
                  : "bg-background text-muted-foreground hover:text-foreground"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      {activeCategory.length === 0 ? (
        <p className="text-muted-foreground py-8 border-y border-border">No earnings recorded for {catLabel}. Confirm or complete some bookings to see this populate.</p>
      ) : (
        <ul className="border-y border-border divide-y divide-border">
          {activeCategory.map((c) => {
            const w = (c.revenue / maxCat) * 100;
            return (
              <li key={c._id || "uncategorized"} data-testid={`earn-cat-${c._id}`} className="py-5">
                <div className="flex justify-between items-baseline mb-2">
                  <span className="font-serif text-lg text-foreground capitalize">{c._id || "Uncategorized"}</span>
                  <span className="text-sm text-primary qw-num">{fmtFcfa(c.revenue)} <span className="text-muted-foreground text-xs ml-2">· {c.bookings} bookings</span></span>
                </div>
                <div className="h-1.5 bg-border relative overflow-hidden">
                  <div className="h-full bg-primary transition-all duration-700" style={{ width: `${w}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


/* ---------------- Inventory ---------------- */
function AdminInventory() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [lowCount, setLowCount] = useState(0);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    name: "", category: "botox", quantity: 0, unit: "vial", low_stock_threshold: 5, notes: "",
  });

  const load = () => {
    axios.get(`${API}/admin/inventory`, { headers: authHeaders(token) })
      .then((r) => { setItems(r.data.items || []); setLowCount(r.data.low_stock_count || 0); })
      .catch(() => {});
  };
  useEffect(load, [token]);

  const create = async () => {
    if (!draft.name.trim()) return;
    await axios.post(`${API}/admin/inventory`, draft, { headers: authHeaders(token) });
    setDraft({ name: "", category: "botox", quantity: 0, unit: "vial", low_stock_threshold: 5, notes: "" });
    setCreating(false);
    load();
  };

  const updateItem = async (id, patch) => {
    await axios.patch(`${API}/admin/inventory/${id}`, patch, { headers: authHeaders(token) });
    load();
  };

  const removeItem = async (id) => {
    if (!window.confirm("Remove this item?")) return;
    await axios.delete(`${API}/admin/inventory/${id}`, { headers: authHeaders(token) });
    load();
  };

  const CATEGORIES = ["botox", "fillers", "iv", "lashes", "brows", "consumable", "other"];
  const UNITS = ["vial", "syringe", "bag", "bottle", "pair", "box", "unit"];

  return (
    <div data-testid="admin-inventory">
      <p className="qw-overline mb-3">Stock</p>
      <div className="flex items-baseline justify-between mb-10 flex-wrap gap-4">
        <h1 className="font-serif text-4xl md:text-5xl text-foreground font-light">Inventory</h1>
        <div className="flex items-center gap-4">
          {lowCount > 0 && (
            <span data-testid="inventory-low-count" className="text-[0.7rem] tracking-wider uppercase px-3 py-1.5 border border-destructive text-destructive bg-destructive/10">
              {lowCount} low stock
            </span>
          )}
          <button
            data-testid="inventory-add-btn"
            onClick={() => setCreating((v) => !v)}
            className="qw-btn qw-btn-primary !py-2.5 !px-5 !text-[0.7rem]"
          >
            {creating ? "Cancel" : "+ Add item"}
          </button>
        </div>
      </div>

      {creating && (
        <div data-testid="inventory-form" className="border border-primary/40 bg-primary/5 p-6 mb-10">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-2">
              <label className="qw-overline block mb-2">Name</label>
              <input data-testid="inv-name" className="qw-input !py-2" value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Botox 100u vial" />
            </div>
            <div>
              <label className="qw-overline block mb-2">Category</label>
              <select data-testid="inv-category" className="qw-input !py-2" value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="qw-overline block mb-2">Quantity</label>
              <input data-testid="inv-qty" type="number" className="qw-input !py-2" value={draft.quantity}
                onChange={(e) => setDraft({ ...draft, quantity: Number(e.target.value) })} />
            </div>
            <div>
              <label className="qw-overline block mb-2">Unit</label>
              <select data-testid="inv-unit" className="qw-input !py-2" value={draft.unit}
                onChange={(e) => setDraft({ ...draft, unit: e.target.value })}>
                {UNITS.map((u) => <option key={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="qw-overline block mb-2">Low at</label>
              <input data-testid="inv-low" type="number" className="qw-input !py-2" value={draft.low_stock_threshold}
                onChange={(e) => setDraft({ ...draft, low_stock_threshold: Number(e.target.value) })} />
            </div>
            <div className="md:col-span-6">
              <label className="qw-overline block mb-2">Notes (optional)</label>
              <input data-testid="inv-notes" className="qw-input !py-2" value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })} placeholder="Brand, expiry, supplier…" />
            </div>
          </div>
          <button data-testid="inv-save-btn" onClick={create} className="qw-btn qw-btn-primary mt-5 !py-2.5 !px-5 !text-[0.7rem]">
            Save item
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <p data-testid="inventory-empty" className="text-muted-foreground py-12 border-y border-border text-center">
          No items yet. Add your first piece of stock — Botox vials, filler syringes, IV bags…
        </p>
      ) : (
        <ul className="divide-y divide-border border-y border-border">
          {items.map((it) => (
            <li key={it.id} data-testid={`inv-row-${it.id}`} className="py-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
              <div className="md:col-span-4">
                <p className="font-serif text-lg text-foreground flex items-center gap-2">
                  {it.name}
                  {it.is_low && (
                    <span data-testid={`inv-low-${it.id}`} className="text-[0.55rem] tracking-wider uppercase px-1.5 py-0.5 bg-destructive/15 text-destructive border border-destructive/30">low</span>
                  )}
                </p>
                {it.notes && <p className="text-xs text-muted-foreground">{it.notes}</p>}
              </div>
              <div className="md:col-span-2 text-xs uppercase tracking-wider text-foreground/70 capitalize">{it.category}</div>
              <div className="md:col-span-3 flex items-center gap-2">
                <button
                  data-testid={`inv-dec-${it.id}`}
                  onClick={() => updateItem(it.id, { quantity: Math.max(0, it.quantity - 1) })}
                  className="h-7 w-7 border border-border text-foreground/85 hover:border-primary hover:text-primary"
                  aria-label="Decrease quantity"
                >−</button>
                <input
                  data-testid={`inv-qty-${it.id}`}
                  type="number"
                  className="w-16 text-center bg-transparent border-b border-border focus:border-primary outline-none font-serif text-primary qw-num text-lg py-1"
                  value={it.quantity}
                  onChange={(e) => updateItem(it.id, { quantity: Math.max(0, Number(e.target.value)) })}
                />
                <button
                  data-testid={`inv-inc-${it.id}`}
                  onClick={() => updateItem(it.id, { quantity: it.quantity + 1 })}
                  className="h-7 w-7 border border-border text-foreground/85 hover:border-primary hover:text-primary"
                  aria-label="Increase quantity"
                >+</button>
                <span className="text-xs text-muted-foreground tracking-wider uppercase">{it.unit}</span>
              </div>
              <div className="md:col-span-2 text-xs text-muted-foreground tracking-wider">
                low at <span className="text-foreground/85">{it.low_stock_threshold}</span>
              </div>
              <div className="md:col-span-1 md:text-right">
                <button
                  data-testid={`inv-del-${it.id}`}
                  onClick={() => removeItem(it.id)}
                  className="text-[0.65rem] tracking-wider uppercase text-muted-foreground hover:text-destructive"
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


/* ---------------- Media Library ---------------- */
function AdminMedia() {
  const { token } = useAuth();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");

  const load = () => {
    axios.get(`${API}/admin/uploads`, { headers: authHeaders(token) })
      .then((r) => setFiles(r.data.uploads || []))
      .catch(() => {});
  };
  useEffect(load, [token]);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Please choose an image file"); return; }
    if (file.size > 8 * 1024 * 1024) { setError("File too large (max 8 MB)"); return; }
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...authHeaders(token), "Content-Type": "multipart/form-data" },
      });
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const del = async (name) => {
    if (!window.confirm("Delete this image? It may still be referenced elsewhere.")) return;
    await axios.delete(`${API}/admin/uploads/${name}`, { headers: authHeaders(token) });
    load();
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(`${BACKEND_URL}${url}`);
      setCopied(url);
      setTimeout(() => setCopied(""), 2000);
    } catch {}
  };

  const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1024*1024 ? `${(b/1024).toFixed(0)} KB` : `${(b/1024/1024).toFixed(1)} MB`;

  return (
    <div data-testid="admin-media">
      <p className="qw-overline mb-3">Assets</p>
      <div className="flex items-baseline justify-between mb-10 flex-wrap gap-4">
        <h1 className="font-serif text-4xl md:text-5xl text-foreground font-light">Media Library</h1>
        <label className="qw-btn qw-btn-primary cursor-pointer" data-testid="media-upload-btn">
          {uploading ? "Uploading…" : "+ Upload image"}
          <input type="file" accept="image/*" className="hidden" data-testid="media-file-input" onChange={(e) => upload(e.target.files?.[0])} />
        </label>
      </div>
      <p className="text-sm text-muted-foreground mb-8 max-w-2xl">
        Upload any image up to 8 MB (JPEG / PNG / WebP / GIF). Once uploaded you can pick it from
        anywhere — Gallery, Site Images in Settings, Testimonials, and more.
      </p>
      {error && <p className="text-sm text-destructive mb-6">{error}</p>}
      {files.length === 0 ? (
        <p data-testid="media-empty" className="text-muted-foreground py-16 text-center border-y border-border">
          No images yet. Upload your first — logo, hero, gallery photos…
        </p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {files.map((f) => (
            <div key={f.filename} data-testid={`media-item-${f.filename}`} className="group border border-border overflow-hidden">
              <div className="aspect-square bg-muted/30 overflow-hidden">
                <img src={`${BACKEND_URL}${f.url}`} alt={f.filename} className="w-full h-full object-cover" />
              </div>
              <div className="p-2.5">
                <p className="text-[0.6rem] tracking-wider text-muted-foreground truncate">{f.filename}</p>
                <p className="text-[0.6rem] text-foreground/60 mt-0.5">{fmtSize(f.size)}</p>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => copyUrl(f.url)}
                    data-testid={`media-copy-${f.filename}`}
                    className="text-[0.6rem] tracking-wider uppercase text-primary hover:underline"
                  >
                    {copied === f.url ? "Copied" : "Copy URL"}
                  </button>
                  <button
                    onClick={() => del(f.filename)}
                    data-testid={`media-delete-${f.filename}`}
                    className="text-[0.6rem] tracking-wider uppercase text-destructive hover:underline ml-auto"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Referrals ---------------- */
function AdminReferrals() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/referrals`, { headers: authHeaders(token) })
      .then((r) => setItems(r.data.referrals || []))
      .catch(() => {});
  }, [token]);

  const totalCredit = items.reduce((sum, r) => sum + (r.credit_fcfa || 0), 0);
  const totalReferrals = items.reduce((sum, r) => sum + (r.successful_referrals || 0), 0);

  return (
    <div data-testid="admin-referrals">
      <p className="qw-overline mb-3">Growth</p>
      <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-10 font-light">Referrals</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border mb-10">
        <div className="bg-background p-6">
          <p className="qw-overline mb-2">Active codes</p>
          <p className="font-serif text-3xl text-primary qw-num">{items.length}</p>
        </div>
        <div className="bg-background p-6">
          <p className="qw-overline mb-2">Successful referrals</p>
          <p className="font-serif text-3xl text-primary qw-num">{totalReferrals}</p>
        </div>
        <div className="bg-background p-6">
          <p className="qw-overline mb-2">Total credit owed</p>
          <p className="font-serif text-3xl text-primary qw-num">{totalCredit.toLocaleString("fr-FR")} FCFA</p>
        </div>
      </div>
      <ul className="divide-y divide-border border-y border-border">
        {items.length === 0 ? (
          <li className="py-6 text-muted-foreground">No referral codes issued yet. They're created lazily when a client visits their account page.</li>
        ) : items.map((r) => (
          <li key={r.code} data-testid={`referral-row-${r.code}`} className="py-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
            <div className="md:col-span-3">
              <p className="font-serif text-lg text-foreground">{r.owner_name || "—"}</p>
              <p className="text-xs text-muted-foreground">{r.owner_email}</p>
            </div>
            <div className="md:col-span-3">
              <p className="font-serif text-primary qw-num tracking-widest">{r.code}</p>
            </div>
            <div className="md:col-span-3 text-sm">
              <p className="text-foreground/85">{r.successful_referrals || 0} referrals</p>
            </div>
            <div className="md:col-span-3 md:text-right">
              <p className="qw-num text-primary">{(r.credit_fcfa || 0).toLocaleString("fr-FR")} FCFA credit</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------- Exit-Intent Leads ---------------- */
function AdminLeads() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/exit-leads`, { headers: authHeaders(token) })
      .then((r) => setItems(r.data.leads || []))
      .catch(() => {});
  }, [token]);
  return (
    <div data-testid="admin-leads">
      <p className="qw-overline mb-3">Signals</p>
      <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-10 font-light">Exit-Intent Leads</h1>
      <p className="text-sm text-muted-foreground mb-8 max-w-2xl">
        Emails captured from the "wait — a little gift" popup. Each was issued a 5% off welcome code.
      </p>
      <ul className="divide-y divide-border border-y border-border">
        {items.length === 0 ? (
          <li className="py-6 text-muted-foreground">No leads yet.</li>
        ) : items.map((l) => (
          <li key={l.email} data-testid={`lead-row-${l.email}`} className="py-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
            <div className="md:col-span-5"><p className="text-foreground">{l.email}</p></div>
            <div className="md:col-span-3"><p className="font-serif text-primary qw-num tracking-widest text-sm">{l.code}</p></div>
            <div className="md:col-span-2 text-xs text-muted-foreground">expires {l.expires_at}</div>
            <div className="md:col-span-2 text-xs text-muted-foreground md:text-right">{(l.created_at || "").split("T")[0]}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------- Change Password ---------------- */
function AdminPassword() {
  const { token } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState({ ok: null, msg: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ ok: null, msg: "" });
    if (next.length < 8) return setStatus({ ok: false, msg: "New password must be at least 8 characters" });
    if (next !== confirm) return setStatus({ ok: false, msg: "New password and confirmation don't match" });
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`,
        { current_password: current, new_password: next },
        { headers: authHeaders(token) });
      setStatus({ ok: true, msg: "Password updated ✓" });
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      setStatus({ ok: false, msg: e.response?.data?.detail || "Could not change password" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-password" className="max-w-lg">
      <p className="qw-overline mb-3">Security</p>
      <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-10 font-light">Change password</h1>
      <form onSubmit={submit} className="space-y-6">
        <div>
          <label className="qw-overline block mb-2">Current password</label>
          <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className="qw-input" data-testid="pw-current" autoComplete="current-password" />
        </div>
        <div>
          <label className="qw-overline block mb-2">New password</label>
          <input type="password" required value={next} onChange={(e) => setNext(e.target.value)} className="qw-input" data-testid="pw-new" autoComplete="new-password" />
          <p className="text-[0.65rem] tracking-wider text-muted-foreground mt-2">Minimum 8 characters</p>
        </div>
        <div>
          <label className="qw-overline block mb-2">Confirm new password</label>
          <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="qw-input" data-testid="pw-confirm" autoComplete="new-password" />
        </div>
        {status.msg && (
          <p data-testid="pw-status" className={`text-sm ${status.ok ? "text-primary" : "text-destructive"}`}>{status.msg}</p>
        )}
        <button type="submit" disabled={saving} data-testid="pw-submit" className="qw-btn qw-btn-primary disabled:opacity-60">
          {saving ? "Saving…" : "Update password"}
        </button>
      </form>
    </div>
  );
}


/* ---------------- Journal (Studio Diary) ---------------- */
const JOURNAL_CATEGORIES = ["editorial", "tips", "aftercare", "announcement"];
const EMPTY_POST = {
  title_en: "", title_fr: "", excerpt_en: "", excerpt_fr: "",
  body_en: "", body_fr: "", cover_image: "",
  category: "editorial", slug: "", published: true,
};

function AdminJournal() {
  const { token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // "new" | postObject | null
  const [saving, setSaving] = useState(false);

  const load = () =>
    axios.get(`${API}/admin/journal`, { headers: authHeaders(token) })
      .then((r) => setPosts(r.data.posts || []))
      .catch(() => {});
  useEffect(() => { load(); }, [token]);

  const startNew = () => setEditing({ ...EMPTY_POST });
  const startEdit = (p) => setEditing({ ...p });

  const save = async (e) => {
    e.preventDefault();
    if (!editing.title_en.trim() || !editing.title_fr.trim()) {
      alert("Please provide both an EN and FR title.");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await axios.patch(`${API}/admin/journal/${editing.id}`, editing, { headers: authHeaders(token) });
      } else {
        await axios.post(`${API}/admin/journal`, editing, { headers: authHeaders(token) });
      }
      setEditing(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not save the post");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    await axios.delete(`${API}/admin/journal/${id}`, { headers: authHeaders(token) });
    load();
  };

  const upd = (k, v) => setEditing((p) => ({ ...p, [k]: v }));

  return (
    <div data-testid="admin-journal">
      <p className="qw-overline mb-3">Studio Diary</p>
      <div className="flex items-baseline justify-between mb-10 flex-wrap gap-4">
        <h1 className="font-serif text-4xl md:text-5xl text-foreground font-light">Journal</h1>
        {!editing && (
          <button data-testid="journal-new-btn" onClick={startNew} className="qw-btn qw-btn-primary !py-2.5 !px-5 !text-[0.7rem]">
            + New post
          </button>
        )}
      </div>

      {editing && (
        <form onSubmit={save} data-testid="journal-form" className="border border-primary/40 bg-primary/5 p-6 md:p-8 mb-12 space-y-6">
          <div className="flex items-baseline justify-between mb-2">
            <p className="qw-overline">{editing.id ? "Edit post" : "New post"}</p>
            <button type="button" onClick={() => setEditing(null)} className="text-[0.65rem] tracking-wider uppercase text-muted-foreground hover:text-destructive">
              Cancel
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field testid="journal-title-en" label="Title (EN)" value={editing.title_en} onChange={(v) => upd("title_en", v)} />
            <Field testid="journal-title-fr" label="Title (FR)" value={editing.title_fr} onChange={(v) => upd("title_fr", v)} />
            <Field testid="journal-excerpt-en" label="Excerpt (EN)" value={editing.excerpt_en} onChange={(v) => upd("excerpt_en", v)} multi />
            <Field testid="journal-excerpt-fr" label="Excerpt (FR)" value={editing.excerpt_fr} onChange={(v) => upd("excerpt_fr", v)} multi />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="qw-overline block mb-2">Body (EN)</label>
              <textarea data-testid="journal-body-en" rows="12" className="qw-input resize-y font-serif text-base leading-relaxed" value={editing.body_en} onChange={(e) => upd("body_en", e.target.value)} placeholder="Write freely. Blank lines separate paragraphs." />
            </div>
            <div>
              <label className="qw-overline block mb-2">Body (FR)</label>
              <textarea data-testid="journal-body-fr" rows="12" className="qw-input resize-y font-serif text-base leading-relaxed" value={editing.body_fr} onChange={(e) => upd("body_fr", e.target.value)} placeholder="Écrivez librement. Les lignes vides séparent les paragraphes." />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ImagePicker testid="journal-cover-picker" label="Cover image (16:9 works best)" value={editing.cover_image} onChange={(v) => upd("cover_image", v)} />
            <div className="space-y-4">
              <div>
                <label className="qw-overline block mb-2">Category</label>
                <select data-testid="journal-category-select" value={editing.category} onChange={(e) => upd("category", e.target.value)} className="qw-input">
                  {JOURNAL_CATEGORIES.map((c) => <option key={c} value={c} className="bg-background">{c}</option>)}
                </select>
              </div>
              <div>
                <label className="qw-overline block mb-2">Custom slug (optional)</label>
                <input data-testid="journal-slug-input" type="text" value={editing.slug || ""} onChange={(e) => upd("slug", e.target.value)} className="qw-input" placeholder="auto-generated from EN title" />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input data-testid="journal-published-toggle" type="checkbox" checked={!!editing.published} onChange={(e) => upd("published", e.target.checked)} className="h-4 w-4 accent-primary" />
                <span className="text-sm text-foreground/85">Publish immediately (visible on public /journal)</span>
              </label>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button type="submit" disabled={saving} data-testid="journal-save-btn" className="qw-btn qw-btn-primary disabled:opacity-60">
              {saving ? "Saving…" : editing.id ? "Update post" : "Publish post"}
            </button>
          </div>
        </form>
      )}

      {!editing && (
        posts.length === 0 ? (
          <p data-testid="journal-empty" className="text-muted-foreground py-16 text-center border-y border-border">
            No posts yet. Click "+ New post" to write your first editorial.
          </p>
        ) : (
          <ul className="divide-y divide-border border-y border-border">
            {posts.map((p) => (
              <li key={p.id} data-testid={`journal-row-${p.slug}`} className="py-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                <div className="md:col-span-1">
                  {p.cover_image ? (
                    <img src={p.cover_image.startsWith("http") ? p.cover_image : `${BACKEND_URL}${p.cover_image}`} alt="" className="w-16 h-16 object-cover" />
                  ) : (
                    <div className="w-16 h-16 bg-muted/40 flex items-center justify-center"><BookOpen size={18} strokeWidth={0.8} className="text-muted-foreground" /></div>
                  )}
                </div>
                <div className="md:col-span-6">
                  <p className="font-serif text-lg text-foreground flex items-center gap-2 flex-wrap">
                    {p.title_en}
                    {!p.published && <span className="text-[0.55rem] tracking-wider uppercase px-1.5 py-0.5 bg-muted/40 text-muted-foreground border border-border">draft</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">/{p.slug}</p>
                </div>
                <div className="md:col-span-2 text-xs uppercase tracking-wider text-foreground/70">{p.category}</div>
                <div className="md:col-span-2 text-xs text-muted-foreground">{(p.published_at || p.created_at || "").split("T")[0]}</div>
                <div className="md:col-span-1 md:text-right space-x-3">
                  <button data-testid={`journal-edit-${p.slug}`} onClick={() => startEdit(p)} className="text-[0.65rem] tracking-wider uppercase text-primary hover:underline">Edit</button>
                  <button data-testid={`journal-delete-${p.slug}`} onClick={() => del(p.id)} className="text-[0.65rem] tracking-wider uppercase text-destructive hover:underline">Delete</button>
                </div>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}


/* ---------------- PAGE EDITOR (per-page structured content) ---------------- */
const PAGE_TABS = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "contact", label: "Contact" },
  { id: "consultation", label: "Consultation" },
  { id: "footer", label: "Footer" },
];

function AdminPages() {
  const { token } = useAuth();
  const { settings, refresh } = useSettings();
  const [tab, setTab] = useState("home");
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraft({ ...settings });
  }, [settings]);

  const upd = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const changed = {};
      for (const k of Object.keys(draft)) {
        if (JSON.stringify(draft[k]) !== JSON.stringify(settings[k])) {
          changed[k] = draft[k];
        }
      }
      if (Object.keys(changed).length === 0) {
        setSaved(true);
        setTimeout(() => setSaved(false), 1800);
        return;
      }
      await axios.put(`${API}/admin/settings`, changed, { headers: authHeaders(token) });
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (e) {
      alert(e.response?.data?.detail || "Could not save changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-pages">
      <p className="qw-overline mb-3">Site content</p>
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-8">
        <h1 className="font-serif text-4xl md:text-5xl text-foreground font-light">Page Editor</h1>
        <div className="flex items-center gap-3">
          {saved && <span data-testid="pages-saved" className="text-primary text-sm">Saved ✓</span>}
          <button data-testid="pages-save-btn" onClick={save} disabled={saving} className="qw-btn qw-btn-primary disabled:opacity-60 !py-2.5 !px-5 !text-[0.7rem]">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-px bg-border border border-border mb-10">
        {PAGE_TABS.map((pt) => (
          <button
            key={pt.id}
            type="button"
            data-testid={`page-tab-${pt.id}`}
            onClick={() => setTab(pt.id)}
            className={`px-5 py-2.5 text-[0.7rem] tracking-wider uppercase transition-colors ${
              tab === pt.id ? "bg-primary text-primary-foreground" : "bg-background text-foreground/85 hover:text-primary"
            }`}
          >
            {pt.label}
          </button>
        ))}
      </div>

      {tab === "home" && <HomePageEditor d={draft} upd={upd} />}
      {tab === "about" && <AboutPageEditor d={draft} upd={upd} />}
      {tab === "services" && <ServicesPageEditor d={draft} upd={upd} />}
      {tab === "contact" && <ContactPageEditor d={draft} upd={upd} />}
      {tab === "consultation" && <ConsultationPageEditor d={draft} upd={upd} />}
      {tab === "footer" && <FooterEditor d={draft} upd={upd} />}
    </div>
  );
}

function BiField({ label, kEn, kFr, d, upd, multi }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Field testid={`page-${kEn}`} label={`${label} (EN)`} value={d[kEn]} onChange={(v) => upd(kEn, v)} multi={multi} />
      <Field testid={`page-${kFr}`} label={`${label} (FR)`} value={d[kFr]} onChange={(v) => upd(kFr, v)} multi={multi} />
    </div>
  );
}

function VisibilityToggle({ label, k, d, upd }) {
  const on = d[k] !== false;
  return (
    <label className="flex items-center gap-3 py-2 cursor-pointer">
      <input data-testid={`toggle-${k}`} type="checkbox" checked={on} onChange={(e) => upd(k, e.target.checked)} className="h-4 w-4 accent-primary" />
      <span className="text-sm text-foreground/85">{label}</span>
    </label>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <div className="border-y border-border py-8 mb-8 space-y-6">
      <div>
        <h3 className="qw-overline mb-2">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground max-w-2xl">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function HomePageEditor({ d, upd }) {
  return (
    <>
      <SectionCard title="Hero" subtitle="The main headline and subtitle at the top of the Home page.">
        <BiField label="Hero title" kEn="hero_title_en" kFr="hero_title_fr" d={d} upd={upd} />
        <BiField label="Hero subtitle" kEn="hero_subtitle_en" kFr="hero_subtitle_fr" d={d} upd={upd} multi />
      </SectionCard>

      <SectionCard title="Section visibility" subtitle="Show or hide entire sections of the Home page.">
        <VisibilityToggle label="Show 'Services preview' section" k="show_home_services" d={d} upd={upd} />
        <VisibilityToggle label="Show 'From the Journal' section" k="show_home_journal" d={d} upd={upd} />
        <VisibilityToggle label="Show 'Testimonials' section" k="show_home_testimonials" d={d} upd={upd} />
      </SectionCard>
    </>
  );
}

function AboutPageEditor({ d, upd }) {
  return (
    <>
      <SectionCard title="About body" subtitle="The main About paragraph shown on the About page.">
        <BiField label="About body" kEn="about_body_en" kFr="about_body_fr" d={d} upd={upd} multi />
      </SectionCard>
      <SectionCard title="Founder bio (optional)" subtitle="If provided, appears in a dedicated 'Founder' section. Leave empty to hide.">
        <BiField label="Founder bio" kEn="about_founder_bio_en" kFr="about_founder_bio_fr" d={d} upd={upd} multi />
        <BiField label="Credentials line" kEn="about_founder_credentials_en" kFr="about_founder_credentials_fr" d={d} upd={upd} />
      </SectionCard>
    </>
  );
}

function ServicesPageEditor({ d, upd }) {
  return (
    <>
      <SectionCard title="Services page intro" subtitle="The paragraph shown below the Services page headline.">
        <BiField label="Intro paragraph" kEn="services_intro_en" kFr="services_intro_fr" d={d} upd={upd} multi />
      </SectionCard>
      <SectionCard title="Manage the services list" subtitle="Reorder services and edit each service in the dedicated tab.">
        <div className="flex gap-3">
          <Link to="/admin/services" className="qw-btn qw-btn-outline !py-2 !px-4 !text-[0.65rem]">Edit prices & content →</Link>
        </div>
      </SectionCard>
    </>
  );
}

function ContactPageEditor({ d, upd }) {
  return (
    <>
      <SectionCard title="Contact intro" subtitle="Paragraph below the Contact page headline.">
        <BiField label="Intro" kEn="contact_intro_en" kFr="contact_intro_fr" d={d} upd={upd} multi />
      </SectionCard>
      <SectionCard title="Contact details" subtitle="Phone, email, WhatsApp — these live in general Settings so they can drive other places on the site too.">
        <Link to="/admin/settings" className="qw-btn qw-btn-outline !py-2 !px-4 !text-[0.65rem]">Edit phone / email / WhatsApp →</Link>
      </SectionCard>
    </>
  );
}

function ConsultationPageEditor({ d, upd }) {
  return (
    <SectionCard title="Consultation intro" subtitle="Currently the 4-question consultation flow uses fixed copy. This is a placeholder for future customisation.">
      <p className="text-xs text-muted-foreground">Nothing to edit yet — coming soon.</p>
    </SectionCard>
  );
}

function FooterEditor({ d, upd }) {
  return (
    <SectionCard title="Footer tagline" subtitle="The small paragraph next to the logo at the bottom of every page.">
      <BiField label="Tagline" kEn="footer_tagline_en" kFr="footer_tagline_fr" d={d} upd={upd} multi />
    </SectionCard>
  );
}

/* ---------------- APPEARANCE (colour palette) ---------------- */
function AdminAppearance() {
  const { token } = useAuth();
  const { settings, refresh } = useSettings();
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraft({
      color_primary_hsl: settings.color_primary_hsl || "",
      color_accent_hsl: settings.color_accent_hsl || "",
      color_background_hsl: settings.color_background_hsl || "",
      font_heading: settings.font_heading || "",
      font_body: settings.font_body || "",
    });
  }, [settings]);

  const upd = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const PRESETS = [
    { name: "Queensway (default)", primary: "", accent: "", background: "" },
    { name: "Rose gold", primary: "24 62% 55%", accent: "45 32% 55%", background: "0 0% 4%" },
    { name: "Emerald", primary: "160 45% 42%", accent: "170 25% 55%", background: "0 0% 5%" },
    { name: "Sapphire", primary: "215 60% 55%", accent: "200 30% 58%", background: "0 0% 4%" },
    { name: "Champagne on ivory", primary: "36 45% 45%", accent: "30 20% 60%", background: "36 30% 96%" },
    { name: "Deep aubergine", primary: "300 30% 55%", accent: "320 25% 65%", background: "290 15% 6%" },
  ];

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await axios.put(`${API}/admin/settings`, draft, { headers: authHeaders(token) });
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (e) {
      alert(e.response?.data?.detail || "Could not save colours");
    } finally {
      setSaving(false);
    }
  };

  const preview = (hsl) => (hsl && hsl.trim() ? `hsl(${hsl})` : "hsl(var(--primary))");

  return (
    <div data-testid="admin-appearance">
      <p className="qw-overline mb-3">Appearance</p>
      <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-10 font-light">Colour palette</h1>
      <p className="text-sm text-muted-foreground max-w-2xl mb-12 leading-relaxed">
        Change the site's primary, accent, and background colours. Values use the HSL format <code className="text-primary">H S% L%</code> (e.g. <code className="text-primary">24 62% 55%</code>).
        Leave a field empty to restore the site default. Applies immediately after saving.
      </p>

      <h3 className="qw-overline mb-4">Presets</h3>
      <div data-testid="palette-presets" className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
        {PRESETS.map((p) => (
          <button
            key={p.name}
            type="button"
            data-testid={`palette-preset-${p.name.replace(/\s+/g, '-').toLowerCase()}`}
            onClick={() => setDraft({ color_primary_hsl: p.primary, color_accent_hsl: p.accent, color_background_hsl: p.background })}
            className="border border-border p-4 text-left hover:border-primary transition-colors"
          >
            <div className="flex gap-2 mb-3">
              <span className="w-6 h-6 rounded-full border border-border" style={{ background: preview(p.primary) }} />
              <span className="w-6 h-6 rounded-full border border-border" style={{ background: preview(p.accent) }} />
              <span className="w-6 h-6 rounded-full border border-border" style={{ background: p.background ? `hsl(${p.background})` : "hsl(var(--background))" }} />
            </div>
            <p className="text-sm text-foreground">{p.name}</p>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
        {[
          { k: "color_primary_hsl", label: "Primary" },
          { k: "color_accent_hsl", label: "Accent" },
          { k: "color_background_hsl", label: "Background" },
        ].map(({ k, label }) => (
          <div key={k}>
            <label className="qw-overline block mb-3">{label}</label>
            <div className="flex items-center gap-3 mb-3">
              <span className="w-10 h-10 rounded-full border border-border shrink-0" style={{ background: draft[k] ? `hsl(${draft[k]})` : `hsl(var(--${k === 'color_primary_hsl' ? 'primary' : k === 'color_accent_hsl' ? 'accent' : 'background'}))` }} />
              <input
                data-testid={`color-input-${k}`}
                type="text"
                value={draft[k] || ""}
                onChange={(e) => upd(k, e.target.value)}
                placeholder="e.g. 24 62% 55%"
                className="qw-input"
              />
            </div>
            <p className="text-[0.6rem] tracking-wider text-muted-foreground">Empty = site default</p>
          </div>
        ))}
      </div>

      <h3 className="qw-overline mb-4">Typography — headline font</h3>
      <div className="max-w-3xl mb-10">
        <FontPicker kind="heading" value={draft.font_heading} onChange={(v) => upd("font_heading", v)} />
      </div>
      <h3 className="qw-overline mb-4">Typography — body font</h3>
      <div className="max-w-3xl mb-12">
        <FontPicker kind="body" value={draft.font_body} onChange={(v) => upd("font_body", v)} />
      </div>

      <div className="flex items-center gap-4">
        <button data-testid="appearance-save-btn" onClick={save} disabled={saving} className="qw-btn qw-btn-primary disabled:opacity-60">
          {saving ? "Applying…" : "Apply palette"}
        </button>
        {saved && <span data-testid="appearance-saved" className="text-primary text-sm">Applied ✓</span>}
      </div>
    </div>
  );
}


/* ---------------- WAIT-LIST ---------------- */
function AdminWaitlist() {
  const { token } = useAuth();
  const { settings, refresh } = useSettings();
  const [entries, setEntries] = useState([]);
  const [capacity, setCapacity] = useState("");
  const [capSaved, setCapSaved] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/waitlist`, { headers: authHeaders(token) })
      .then((r) => setEntries(r.data.entries || [])).catch(() => {});
  };
  useEffect(load, [token]);
  useEffect(() => {
    setCapacity(String(settings.daily_booking_capacity ?? 6));
  }, [settings.daily_booking_capacity]);

  const saveCapacity = async () => {
    await axios.put(`${API}/admin/settings`, { daily_booking_capacity: parseInt(capacity || "0", 10) }, { headers: authHeaders(token) });
    await refresh();
    setCapSaved(true);
    setTimeout(() => setCapSaved(false), 2200);
  };

  const setStatus = async (id, status) => {
    await axios.patch(`${API}/admin/waitlist/${id}`, { status }, { headers: authHeaders(token) });
    load();
  };

  const waMessage = (e) =>
    e.language === "fr"
      ? `Bonjour ${e.full_name}, ici Queensway Studio — une place s'est libérée le ${e.date}${e.service_name ? ` pour ${e.service_name}` : ""}. Souhaitez-vous réserver ?`
      : `Hello ${e.full_name}, this is Queensway Studio — a spot just opened on ${e.date}${e.service_name ? ` for ${e.service_name}` : ""}. Would you like to book?`;

  const STATUS_STYLE = {
    waiting: "border-muted-foreground text-muted-foreground",
    notified: "border-primary text-primary",
    booked: "border-accent text-accent",
    removed: "border-border text-foreground/40",
  };

  return (
    <div data-testid="admin-waitlist">
      <p className="qw-overline mb-3">Reservations</p>
      <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-10 font-light">Wait-list</h1>

      <div className="border border-border p-6 mb-12 max-w-xl">
        <p className="qw-overline mb-3">Daily booking capacity</p>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          When a day reaches this many pending/confirmed bookings, new clients are offered the wait-list instead. Set 0 for unlimited.
        </p>
        <div className="flex items-center gap-3">
          <input data-testid="capacity-input" type="number" min="0" max="50" value={capacity}
            onChange={(e) => setCapacity(e.target.value)} className="qw-input !w-28" />
          <button data-testid="capacity-save-btn" onClick={saveCapacity} className="qw-btn qw-btn-primary !py-2.5 !px-5 !text-[0.7rem]">Save</button>
          {capSaved && <span data-testid="capacity-saved" className="text-primary text-sm">Saved ✓</span>}
        </div>
      </div>

      <ul className="divide-y divide-border border-y border-border">
        {entries.length === 0 && <li className="py-6 text-muted-foreground">No wait-list entries yet.</li>}
        {entries.map((e) => (
          <li key={e.id} data-testid={`waitlist-entry-${e.id}`} className="py-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
            <div className="md:col-span-4">
              <p className="font-serif text-lg text-foreground flex items-center gap-2">
                {e.full_name}
                {e.is_member && (
                  <span className="inline-flex items-center gap-1 text-[0.6rem] tracking-wider uppercase px-2 py-0.5 bg-primary/15 text-primary border border-primary/30"><Crown size={9} strokeWidth={1.4} /> Priority</span>
                )}
              </p>
              <p className="text-xs text-muted-foreground">{e.phone}{e.email ? ` · ${e.email}` : ""}</p>
              {e.service_name && <p className="text-[0.7rem] text-primary mt-1">{e.service_name}</p>}
            </div>
            <div className="md:col-span-3 text-sm text-foreground/85">
              <p className="qw-num">{e.date}</p>
              <p className="text-[0.65rem] text-muted-foreground mt-1">joined {new Date(e.created_at).toLocaleDateString("en-GB")}</p>
            </div>
            <div className="md:col-span-5 flex flex-wrap gap-2 items-center md:justify-end">
              <span className={`text-[0.65rem] tracking-wider uppercase px-2 py-1 border ${STATUS_STYLE[e.status] || ""}`}>{e.status}</span>
              <a
                href={`https://wa.me/${e.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(waMessage(e))}`}
                target="_blank" rel="noreferrer"
                data-testid={`waitlist-whatsapp-${e.id}`}
                className="text-[0.65rem] tracking-wider uppercase px-3 py-1.5 border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                WhatsApp
              </a>
              {e.status === "waiting" && (
                <button data-testid={`waitlist-notify-${e.id}`} onClick={() => setStatus(e.id, "notified")}
                  className="text-[0.65rem] tracking-wider uppercase px-3 py-1.5 border border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-colors">
                  Mark notified
                </button>
              )}
              {e.status !== "booked" && e.status !== "removed" && (
                <button data-testid={`waitlist-booked-${e.id}`} onClick={() => setStatus(e.id, "booked")}
                  className="text-[0.65rem] tracking-wider uppercase px-3 py-1.5 border border-border text-foreground/70 hover:border-primary hover:text-primary transition-colors">
                  Booked
                </button>
              )}
              {e.status !== "removed" && (
                <button data-testid={`waitlist-remove-${e.id}`} onClick={() => setStatus(e.id, "removed")}
                  className="h-7 w-7 border border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground flex items-center justify-center">
                  <X size={12} strokeWidth={1.2} />
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------- INNER CIRCLE MEMBERS ---------------- */
function AdminMembers() {
  const { token } = useAuth();
  const { settings, refresh } = useSettings();
  const [members, setMembers] = useState([]);
  const [fee, setFee] = useState("");
  const [pct, setPct] = useState("");
  const [saved, setSaved] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/memberships`, { headers: authHeaders(token) })
      .then((r) => setMembers(r.data.members || [])).catch(() => {});
  };
  useEffect(load, [token]);
  useEffect(() => {
    setFee(String(settings.membership_fee_fcfa ?? 150000));
    setPct(String(settings.membership_discount_pct ?? 10));
  }, [settings.membership_fee_fcfa, settings.membership_discount_pct]);

  const saveSettings = async () => {
    await axios.put(`${API}/admin/settings`, {
      membership_fee_fcfa: parseInt(fee || "0", 10),
      membership_discount_pct: parseInt(pct || "0", 10),
    }, { headers: authHeaders(token) });
    await refresh();
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  };

  const act = async (userId, action) => {
    await axios.patch(`${API}/admin/memberships/${userId}`, { action }, { headers: authHeaders(token) });
    load();
  };

  const requested = members.filter((m) => m.membership_status === "requested");
  const active = members.filter((m) => m.membership_status === "active");
  const expired = members.filter((m) => m.membership_status === "expired");

  const Row = ({ m, children }) => (
    <li data-testid={`member-row-${m.id}`} className="py-5 flex flex-wrap gap-4 items-center justify-between">
      <div>
        <p className="font-serif text-lg text-foreground">{m.name}</p>
        <p className="text-xs text-muted-foreground">{m.email}</p>
        {m.membership_expires && (
          <p className="text-[0.65rem] text-primary mt-1">expires {m.membership_expires}</p>
        )}
      </div>
      <div className="flex gap-2">{children}</div>
    </li>
  );

  return (
    <div data-testid="admin-members">
      <p className="qw-overline mb-3">Loyalty</p>
      <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-10 font-light flex items-center gap-4">
        <Crown size={30} strokeWidth={1} className="text-primary" /> Inner Circle
      </h1>

      <div className="border border-border p-6 mb-12 max-w-xl">
        <p className="qw-overline mb-4">Membership settings</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
          <div>
            <label className="text-[0.6rem] tracking-widest uppercase text-muted-foreground block mb-2">Annual fee (FCFA)</label>
            <input data-testid="membership-fee-input" type="number" min="0" value={fee} onChange={(e) => setFee(e.target.value)} className="qw-input" />
          </div>
          <div>
            <label className="text-[0.6rem] tracking-widest uppercase text-muted-foreground block mb-2">Member discount (%)</label>
            <input data-testid="membership-pct-input" type="number" min="0" max="50" value={pct} onChange={(e) => setPct(e.target.value)} className="qw-input" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="membership-settings-save-btn" onClick={saveSettings} className="qw-btn qw-btn-primary !py-2.5 !px-5 !text-[0.7rem]">Save</button>
          {saved && <span className="text-primary text-sm">Saved ✓</span>}
        </div>
      </div>

      <h3 className="qw-overline mb-4">Pending requests ({requested.length})</h3>
      <ul className="divide-y divide-border border-y border-border mb-12">
        {requested.length === 0 && <li className="py-5 text-muted-foreground text-sm">No pending requests.</li>}
        {requested.map((m) => (
          <Row key={m.id} m={m}>
            <button data-testid={`member-activate-${m.id}`} onClick={() => act(m.id, "activate")}
              className="text-[0.65rem] tracking-wider uppercase px-3 py-1.5 border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors">
              Payment received — Activate
            </button>
            <button data-testid={`member-decline-${m.id}`} onClick={() => act(m.id, "decline")}
              className="text-[0.65rem] tracking-wider uppercase px-3 py-1.5 border border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground transition-colors">
              Decline
            </button>
          </Row>
        ))}
      </ul>

      <h3 className="qw-overline mb-4">Active members ({active.length})</h3>
      <ul className="divide-y divide-border border-y border-border mb-12">
        {active.length === 0 && <li className="py-5 text-muted-foreground text-sm">No active members yet.</li>}
        {active.map((m) => (
          <Row key={m.id} m={m}>
            <button data-testid={`member-revoke-${m.id}`} onClick={() => act(m.id, "revoke")}
              className="text-[0.65rem] tracking-wider uppercase px-3 py-1.5 border border-border text-foreground/70 hover:border-destructive hover:text-destructive transition-colors">
              Revoke
            </button>
          </Row>
        ))}
      </ul>

      {expired.length > 0 && (
        <>
          <h3 className="qw-overline mb-4">Expired ({expired.length})</h3>
          <ul className="divide-y divide-border border-y border-border">
            {expired.map((m) => (
              <Row key={m.id} m={m}>
                <button data-testid={`member-renew-${m.id}`} onClick={() => act(m.id, "activate")}
                  className="text-[0.65rem] tracking-wider uppercase px-3 py-1.5 border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors">
                  Renew
                </button>
              </Row>
            ))}
          </ul>
        </>
      )}

      <div className="mt-16">
        <MemberOffers />
      </div>
    </div>
  );
}

/* ---------------- MEMBERS-ONLY OFFERS ---------------- */
const EMPTY_OFFER = { title_en: "", title_fr: "", body_en: "", body_fr: "", kind: "seasonal", valid_until: "", is_active: true };

function MemberOffers() {
  const { token } = useAuth();
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState(EMPTY_OFFER);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/member-offers`, { headers: authHeaders(token) })
      .then((r) => setOffers(r.data.offers || [])).catch(() => {});
  };
  useEffect(load, [token]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title_en.trim() || !form.title_fr.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, valid_until: form.valid_until || null };
      if (editingId) {
        await axios.put(`${API}/admin/member-offers/${editingId}`, payload, { headers: authHeaders(token) });
      } else {
        await axios.post(`${API}/admin/member-offers`, payload, { headers: authHeaders(token) });
      }
      setForm(EMPTY_OFFER);
      setEditingId(null);
      load();
    } finally { setSaving(false); }
  };

  const edit = (o) => {
    setEditingId(o.id);
    setForm({ title_en: o.title_en, title_fr: o.title_fr, body_en: o.body_en || "", body_fr: o.body_fr || "", kind: o.kind, valid_until: o.valid_until || "", is_active: o.is_active });
  };

  const toggleActive = async (o) => {
    await axios.put(`${API}/admin/member-offers/${o.id}`, {
      title_en: o.title_en, title_fr: o.title_fr, body_en: o.body_en || "", body_fr: o.body_fr || "",
      kind: o.kind, valid_until: o.valid_until || null, is_active: !o.is_active,
    }, { headers: authHeaders(token) });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this offer? This cannot be undone.")) return;
    await axios.delete(`${API}/admin/member-offers/${id}`, { headers: authHeaders(token) });
    if (editingId === id) { setEditingId(null); setForm(EMPTY_OFFER); }
    load();
  };

  return (
    <div data-testid="admin-member-offers">
      <h3 className="qw-overline mb-2">Members-only offers</h3>
      <p className="text-xs text-muted-foreground mb-6 max-w-xl leading-relaxed">
        Seasonal offers and early-access announcements shown only to active Inner Circle members in their account.
      </p>

      <div className="border border-border p-6 mb-10 max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
          <div>
            <label className="text-[0.6rem] tracking-widest uppercase text-muted-foreground block mb-2">Title (EN)</label>
            <input data-testid="offer-title-en" value={form.title_en} onChange={(e) => upd("title_en", e.target.value)} className="qw-input" />
          </div>
          <div>
            <label className="text-[0.6rem] tracking-widest uppercase text-muted-foreground block mb-2">Titre (FR)</label>
            <input data-testid="offer-title-fr" value={form.title_fr} onChange={(e) => upd("title_fr", e.target.value)} className="qw-input" />
          </div>
          <div>
            <label className="text-[0.6rem] tracking-widest uppercase text-muted-foreground block mb-2">Description (EN)</label>
            <textarea data-testid="offer-body-en" rows="3" value={form.body_en} onChange={(e) => upd("body_en", e.target.value)} className="qw-input resize-y" />
          </div>
          <div>
            <label className="text-[0.6rem] tracking-widest uppercase text-muted-foreground block mb-2">Description (FR)</label>
            <textarea data-testid="offer-body-fr" rows="3" value={form.body_fr} onChange={(e) => upd("body_fr", e.target.value)} className="qw-input resize-y" />
          </div>
          <div>
            <label className="text-[0.6rem] tracking-widest uppercase text-muted-foreground block mb-2">Type</label>
            <select data-testid="offer-kind" value={form.kind} onChange={(e) => upd("kind", e.target.value)} className="qw-input">
              <option value="seasonal">Seasonal offer</option>
              <option value="early_access">Early access</option>
            </select>
          </div>
          <div>
            <label className="text-[0.6rem] tracking-widest uppercase text-muted-foreground block mb-2">Valid until (optional)</label>
            <input data-testid="offer-valid-until" type="date" value={form.valid_until} onChange={(e) => upd("valid_until", e.target.value)} className="qw-input" />
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button data-testid="offer-save-btn" onClick={save} disabled={saving || !form.title_en.trim() || !form.title_fr.trim()}
            className="qw-btn qw-btn-primary !py-2.5 !px-5 !text-[0.7rem] disabled:opacity-50">
            {editingId ? "Update offer" : "Add offer"}
          </button>
          {editingId && (
            <button onClick={() => { setEditingId(null); setForm(EMPTY_OFFER); }} className="text-[0.65rem] tracking-wider uppercase text-foreground/60 hover:text-primary">
              Cancel edit
            </button>
          )}
        </div>
      </div>

      <ul className="divide-y divide-border border-y border-border max-w-3xl">
        {offers.length === 0 && <li className="py-5 text-muted-foreground text-sm">No offers yet.</li>}
        {offers.map((o) => (
          <li key={o.id} data-testid={`admin-offer-${o.id}`} className="py-5 flex flex-wrap gap-4 items-center justify-between">
            <div>
              <p className="font-serif text-lg text-foreground flex items-center gap-2">
                {o.title_en}
                <span className={`text-[0.6rem] tracking-wider uppercase px-2 py-0.5 ${o.kind === "early_access" ? "bg-primary text-primary-foreground border border-primary" : "border border-primary text-primary"}`}>
                  {o.kind === "early_access" ? "Early access" : "Seasonal"}
                </span>
                {!o.is_active && <span className="text-[0.6rem] tracking-wider uppercase px-2 py-0.5 border border-border text-foreground/40">Hidden</span>}
              </p>
              <p className="text-xs text-muted-foreground">{o.title_fr}{o.valid_until ? ` · until ${o.valid_until}` : ""}</p>
            </div>
            <div className="flex gap-2">
              <button data-testid={`offer-toggle-${o.id}`} onClick={() => toggleActive(o)}
                className="text-[0.65rem] tracking-wider uppercase px-3 py-1.5 border border-border text-foreground/70 hover:border-primary hover:text-primary transition-colors">
                {o.is_active ? "Hide" : "Show"}
              </button>
              <button data-testid={`offer-edit-${o.id}`} onClick={() => edit(o)}
                className="text-[0.65rem] tracking-wider uppercase px-3 py-1.5 border border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-colors">
                Edit
              </button>
              <button data-testid={`offer-delete-${o.id}`} onClick={() => remove(o.id)}
                className="h-7 w-7 border border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground flex items-center justify-center">
                <X size={12} strokeWidth={1.2} />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
